/**
 * LegalPage — shared chrome for /legal/* routes.
 *
 * Public surface (anonymous + authed) — wraps content in:
 *   - Wordmark header linking home (no app nav — these pages are reachable
 *     from sign-up + footer + cookie banner, so the user may not be authed).
 *   - Editorial container width matching design.md § Editorial mode.
 *   - Footer with Privacy/Terms/Cookies + Cookie preferences trigger.
 *
 * DRAFT banner is rendered above the page when `draft` prop is true — every
 * pre-launch legal doc starts as a draft until SME / legal counsel ratifies
 * (J-X13 § Required affordances: "placeholder OK during development build
 * with clear 'draft — not for production' notice").
 *
 * Inv 14 — internal nav via <Link>; Inv 15 — Heading / Eyebrow / Link atoms
 * from `@/components/ui` barrel.
 */

import type { ReactElement, ReactNode } from 'react';
import { Eyebrow, Heading, Link } from '@/components/ui';
import { Footer } from '../../components/app-shell/Footer.tsx';

export interface LegalPageProps {
  eyebrow: string;
  title: string;
  version: string;
  draft?: boolean;
  children: ReactNode;
}

export function LegalPage({
  eyebrow,
  title,
  version,
  draft = false,
  children,
}: LegalPageProps): ReactElement {
  return (
    <div className="min-h-screen bg-bg text-ink flex flex-col">
      <header role="banner" className="border-b border-line bg-card">
        <div className="mx-auto flex max-w-page items-center justify-between px-page-pad-x py-5">
          <Link
            to="/"
            aria-label="BIMAssess home"
            className="font-sans text-lg font-medium tracking-tight no-underline"
          >
            BIMAssess
          </Link>
        </div>
      </header>
      <main role="main" className="flex-1">
        <article className="mx-auto max-w-3xl px-6 py-12">
          <Eyebrow>{eyebrow}</Eyebrow>
          <Heading level="h1" className="mt-3">
            {title}
          </Heading>
          <p className="mt-4 font-mono text-mono text-muted-2">
            Version {version}
          </p>
          {draft ? (
            <div
              role="note"
              aria-label="Draft notice"
              className="mt-6 rounded-md border border-line bg-bg-warm px-4 py-3 font-mono text-mono text-ink"
            >
              <strong>Draft — not for production.</strong> This document is a
              placeholder pending review by SME / legal counsel before launch.
            </div>
          ) : null}
          <div className="mt-8 space-y-4 font-sans text-body text-ink">
            {children}
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
